// ─────────────────────────────────────────────────────────────────────────────
// THE 24-HOUR CHART CHECK — a unit manager's morning, on one screen.
//
//   "Unit managers — generally this is the process regardless of what company — there should
//    be some level of chart checks. The unit manager is looking back 24 hours to see the
//    activity that happened and only focus on those charts. So if I have a 20-bed unit and 10
//    of my patients had orders written on their record for the previous 24 hours, those are
//    the charts I need to focus on."
//
//   "They're looking at the order, they're looking at the progress note, and any labs or
//    results that have come in for that patient. Those are usually the three components that
//    help make sure that everything is driving well." … "They're all independent."
//
//   Asked whether managers choose their ten: "You don't pick. That's the list you work with."
//
// ── FOUR DESIGN RULES, ALL OF THEM FROM SOMETHING SHE SAID ───────────────────
//
// 1. THE LABS COLUMN SAYS IT IS NOT BEING CHECKED. Her own condition for accepting a
//    two-leg version. It is a column, not a footnote, and it is there on an empty building.
// 2. NO RANKING, NO SORT, NO CUT-OFF. "You don't pick." The list is the list. There is no
//    severity score on a row and the order is the server's.
// 3. ONE LINE PER CHART, DETAIL ON CLICK. "It was very overwhelming when you look at
//    everything on the screen … it could lead to non-usage or under-usage." Eight charts
//    with three legs each is twenty-four checks; shown at once it is a wall.
// 4. THE STALENESS IS ON SCREEN. This reads our own mirror, written by the nightly scan, so
//    the list is as fresh as the last scan and never fresher. A screen that implies live
//    data when it is showing Tuesday's is the silent failure this product is built against.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchChartCheck, markChartReviewed, unmarkChartReviewed } from "../postIncidentAudit/postIncidentAuditApi";
import {
  loadSampleReviewed, toggleSampleReviewed, loadSampleDispositions, setSampleDisposition,
} from "./sampleReviewed";
import SAMPLE from "../demo/chartCheckSample.json";
import {
  LEG_STATE, LEG_STATE_ORDER, legMark, chartCheckHeadline, residentLabel, findingLines,
  alertWouldReport,
} from "./chartCheckLegs";
import DispositionPicker from "./DispositionPicker";
import ChartCheckExplainer from "./ChartCheckExplainer";

const T = { micro: 10.5, xs: 12, sm: 13.5, base: 14, md: 15, lg: 16, xl: 18 };
const LH = { tight: 1.4, body: 1.55 };

const WINDOWS = [
  { hours: 24, label: "24 hours" },
  { hours: 48, label: "48 hours" },
  { hours: 72, label: "3 days" },
  { hours: 168, label: "7 days" },
];

// One definition, used by the header and by every row — a header whose columns can drift
// from the rows' is worse than no header.
const ROW_COLUMNS = "minmax(170px, 1.4fr) repeat(3, minmax(104px, auto)) minmax(120px, 1fr)";
const REVIEWED_COL = 132;

/** One leg's state, as a mark. Glyph, then word; colour is the third signal, never the first. */
const LegMark = ({ state, title, blocked = 0, blockedScopes = [] }) => {
  const m = legMark(state);
  return (
    <span
      title={title || m.meaning}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 8px",
        borderRadius: 6,
        // POSITIONED so the blocked suffix's screen-reader text, which is absolutely
        // positioned and clipped, anchors here instead of to the nearest positioned
        // ancestor somewhere up the page — where a 1px clipped span can still drag
        // scroll position on focus.
        position: "relative",
        background: m.tone.bg,
        color: m.tone.fg,
        border: `1px solid ${m.tone.border}`,
        fontSize: T.xs,
        fontWeight: 700,
        lineHeight: 1.35,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ fontWeight: 800 }}>{m.glyph}</span>
      {m.word}
      {/* ── WHAT WE COULD NOT LOOK AT, BESIDE WHAT WE FOUND ──────────────────
          Two different facts, and one mark could not hold both. The orders column
          always carries two blocked checks — the care-plan question and the rest of
          the order book, both waiting on November 2026 — so a rolled-up state could
          never reach Clear or Read and showed "Not checked" on every chart. This
          suffix is the blocked half, so the found half can be the mark itself. */}
      {blocked > 0 && (
        <span
          title={`${blocked} check${blocked === 1 ? "" : "s"} could not be made: ${blockedScopes.join(", ")}`}
          style={{
            marginLeft: 2, paddingLeft: 6, borderLeft: `1px solid ${m.tone.border}`,
            fontWeight: 700, opacity: 0.85,
          }}
        >
          <span aria-hidden="true">{"⊘"}</span>
          <span style={{ marginLeft: 2 }}>{blocked}</span>
          {/* The glyph and number are decoration; this is the sentence a screen reader
              gets, and it names the grant rather than only counting it. */}
          <span style={{
            position: "absolute", width: 1, height: 1, overflow: "hidden",
            clip: "rect(0 0 0 0)", whiteSpace: "nowrap",
          }}>
            {` — ${blocked} check${blocked === 1 ? "" : "s"} not made: ${blockedScopes.join(", ")}`}
          </span>
        </span>
      )}
    </span>
  );
};

/**
 * "I reviewed this chart."
 *
 * ── WHAT THIS IS NOT ────────────────────────────────────────────────────────
 *
 * It is not an answer. Ticking it does not make the order leg clear, does not touch the labs
 * column, and enters no percentage anywhere. A tick means a PERSON LOOKED. The moment it
 * carried a Y/N it would be a second, shallower audit trail competing with the post-incident
 * worksheet's — which is signed, accountable, and the thing a surveyor is handed.
 *
 * It is also not a delete. The chart stays on the list reading as done, because removing it
 * would take away the only way she can check her own work.
 *
 * The word changes rather than only the colour, for the same reason every mark on these
 * screens carries a word: this gets looked at by someone nine hours into a shift, and on a
 * monitor that may be rendering both states as the same grey.
 */
const ReviewedButton = ({ reviewed, busy, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    title={reviewed ? "Recorded as reviewed \u2014 click to take it back" : "Record that you have reviewed this chart"}
    aria-pressed={!!reviewed}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      width: "100%",
      justifyContent: "center",
      padding: "5px 9px",
      borderRadius: 8,
      fontSize: T.xs,
      fontWeight: 700,
      fontFamily: "inherit",
      cursor: busy ? "default" : "pointer",
      opacity: busy ? 0.55 : 1,
      background: reviewed ? "#dcfce7" : "var(--ed-canvas)",
      color: reviewed ? "#14532d" : "var(--ed-ink-2)",
      border: `1px solid ${reviewed ? "#86efac" : "var(--ed-line)"}`,
    }}
  >
    <span aria-hidden="true" style={{ fontWeight: 800 }}>{reviewed ? "\u2713" : "\u25CB"}</span>
    {reviewed ? "Reviewed" : "Not reviewed"}
  </button>
);

/** What the four marks mean, said once. */
const StateLegend = () => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "7px 16px", alignItems: "center", fontSize: T.xs, color: "var(--ed-muted)" }}>
    {LEG_STATE_ORDER.map((s) => {
      const m = legMark(s);
      return (
        <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <LegMark state={s} />
          <span style={{ lineHeight: LH.body }}>{m.meaning}</span>
        </span>
      );
    })}
  </div>
);

/**
 * One check, opened.
 *
 * A blocked check prints the grant it waits on and WHEN. "Blocked" and "reads from November
 * 2026" are different things to tell a nurse and only one of them is useful; a scope with no
 * date says that out loud rather than leaving an empty space that reads as "soon".
 */
// ── THE HIGH-ALERT MARK ───────────────────────────────────────────────────────
//
// A new order in a high-alert class — an anticoagulant, an opioid, insulin — is the one
// line on this screen that has to be seen by somebody scanning rather than reading. So it
// moves.
//
// ── A PULSE, NOT A BLINK, AND THE SAME ONE THE WORKSHEET USES ───────────────
//
// A hard flash at speed is harder to read than a slow pulse and it is a seizure risk; this
// is a 1.7s ease cycle, well under the 3Hz threshold. It stops entirely for anyone whose
// system asks for less motion, and that costs nothing because the glyph, the word
// "high-alert" and the colour all still carry the meaning — the motion is the fourth
// signal, never the only one. Same shape as `pia-gap-mark` on the restraint worksheet and
// `survai-pulse` on the outstanding-MDS badge.
//
// ── IT DOES NOT CHANGE THE CHECK'S STATE, AND THAT IS DELIBERATE ────────────
//
// The high-risk check is TO_READ, not ATTENTION, and must stay so: making it amber turned
// the attention count into the row count, which is the overwhelm this screen was warned
// about twice. This is EMPHASIS, not a verdict. Amber rather than red for the same reason —
// the check's own sentence says "not because anything is wrong with it", and a red mark
// over that sentence would be the honest-prose-under-a-wrong-state defect in colour.
const HIGH_RISK_CSS = `
@keyframes cc-highrisk-pulse {
  0%, 100% { opacity: 1;   box-shadow: 0 0 0 0 rgba(180, 83, 9, .5); }
  50%      { opacity: .68; box-shadow: 0 0 0 6px rgba(180, 83, 9, 0); }
}
.cc-highrisk { animation: cc-highrisk-pulse 1.7s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .cc-highrisk { animation: none; } }
`;

const HighRiskMark = ({ compact = false }) => (
  <span
    className="cc-highrisk"
    // THE WORD IS IN THE LABEL, not only in the glyph. A screen reader announcing "!" has
    // conveyed nothing, and an exclamation mark alone is what every other badge on every
    // other product also looks like.
    role="img"
    aria-label="High-alert medication class — read this order"
    title="A new order in a high-alert medication class. Confirm the indication and any monitoring are on the chart."
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "0 0 auto",
      width: compact ? 17 : 19,
      height: compact ? 17 : 19,
      borderRadius: 999,
      // FULLY TOKENISED, DELIBERATELY, rather than by adding #fef3c7 to the literal map.
      // The conversion only maps a literal that is EXACTLY a default-preset value, so the
      // mark came out of it half themed: amber ink over a Tailwind amber-100 ground that no
      // token names. A half-themed warning badge is worse than an unthemed one — under the
      // paper-form preset it would be amber ink on a ground that is not from that palette.
      background: "var(--ed-amber-soft)",
      color: "var(--ed-amber-deep)",
      border: "1px solid var(--ed-amber-line)",
      fontSize: compact ? 11 : 12,
      fontWeight: 900,
      lineHeight: 1,
    }}
  >
    !
  </span>
);

/** Is this the high-alert-class check? Asked by id, never by state — see HIGH_RISK_CSS. */
const isHighRisk = (check) => check?.id === "high-risk";

const CheckLine = ({ check }) => {
  const m = legMark(check.state);
  const hot = isHighRisk(check);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" }}>
      <span style={{ flex: "0 0 auto", marginTop: 1 }}><LegMark state={check.state} /></span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex", gap: 7, alignItems: "center",
            fontSize: T.sm, color: "var(--ed-ink)", fontWeight: 600, lineHeight: LH.tight,
          }}
        >
          {hot ? <HighRiskMark /> : null}
          <span style={{ minWidth: 0 }}>{check.question}</span>
        </div>
        <div style={{ fontSize: T.sm, color: "var(--ed-ink-2)", lineHeight: LH.body, marginTop: 2 }}>{check.because}</div>
        {!m.wasChecked && check.scopeLabel ? (
          <div style={{ fontSize: T.xs, color: "#713f12", marginTop: 3, lineHeight: LH.body }}>
            Waiting on <strong>{check.scopeLabel}</strong>
            {check.grantable === false
              ? " — there is no endpoint for this, so no grant would fix it."
              : check.availableFrom
                ? ` — expected ${check.availableFrom}.`
                : " — no date is set for this grant."}
            {check.unlocks ? <span style={{ color: "var(--ed-amber-deep)" }}>{" "}It would answer: {check.unlocks}</span> : null}
          </div>
        ) : null}
        <CheckEvidence check={check} />
      </div>
    </div>
  );
};

/** The named orders behind a finding. Without them the check is an assertion. */
const CheckEvidence = ({ check }) => {
  const e = check.evidence;
  if (!e) return null;
  const rows = [];
  for (const x of e.incomplete || []) rows.push(`${x.order} — missing ${x.missing.join(", ")}`);
  for (const x of e.withoutHold || []) rows.push(`${x.order} (${x.drugClass}) — no ${x.wants} stated`);
  for (const x of e.overlaps || []) {
    rows.push(
      `${x.a.order} covers pain ${x.a.range.lo}–${x.a.range.hi} and ${x.b.order} covers ` +
      `${x.b.range.lo}–${x.b.range.hi}; a score of ${x.ambiguousScores.join(" or ")} fits both`,
    );
  }
  // The notes are listed by date and type, never by text: the note is read in the chart.
  for (const n of e.notes || []) rows.push(`${String(n.date || "").slice(0, 16).replace("T", " ")} · ${n.type || "note"}`);
  if (!rows.length) return null;
  return (
    <ul style={{ margin: "5px 0 0", paddingLeft: 17, fontSize: T.xs, color: "var(--ed-ink-2)", lineHeight: LH.body }}>
      {rows.map((r, i) => <li key={i}>{r}</li>)}
    </ul>
  );
};


/**
 * One chart, as one line.
 *
 * The three marks ARE the disclosure control, because after seeing a mark the next question
 * is always why.
 */
const ChartRow = ({
  row, index = 0, roster, legOrder, reviewed, onToggleReviewed, busy,
  disposition = null, dispositionNote = null, dispositionUnsaved = null, onChooseDisposition,
}) => {
  const [open, setOpen] = useState(false);
  const who = residentLabel(row.residentId, roster);
  // Every ATTENTION check across every leg, as sentences. Hidden when the row is open, since
  // the detail below repeats them in full with their questions.
  const findings = legOrder.flatMap((k) => findingLines(row.legs?.[k]));
  // ── THE HIGH-ALERT ORDERS, ON THE COLLAPSED ROW ──────────────────────────
  //
  // findingLines() returns ATTENTION checks only, and the high-risk check is TO_READ by
  // design — so the drug's name lived behind the expander, which is where the original
  // complaint came from: "I put in a high risk medication, which was not flagged there."
  // A pulsing mark inside a closed row is a mark nobody sees.
  //
  // Named, not counted. "1 high-alert order" sends her to the expander to find out which;
  // "Warfarin Sodium (anticoagulant)" is the thing she acts on.
  const highRisk = legOrder.flatMap((k) =>
    (row.legs?.[k]?.checks || [])
      .filter((c) => isHighRisk(c))
      .flatMap((c) => c.evidence?.highRisk || []),
  );
  return (
    // ── WHERE ONE PATIENT ENDS AND THE NEXT BEGINS ───────────────────────────
    //
    // This was a single 1px --ed-line between rows, which is #e7e4dd on #ffffff —
    // about 1.2:1, i.e. invisible at arm's length under fluorescent light. On a
    // collapsed list that merely looks soft. With a row OPEN it is a genuine
    // safety problem: the expanded detail runs twenty lines of findings, scope
    // notes and progress notes with no containment, so the reader cannot tell
    // where one resident's chart stops and the next one's begins. On a screen
    // whose entire output is per-resident clinical findings, attributing a
    // finding to the wrong resident is the worst thing it can do.
    //
    // Three separations, because one was not enough and each does a different job:
    //   · a real rule between residents, in --ed-line-hover rather than --ed-line
    //   · a zebra on alternate residents, so the eye tracks a row across its
    //     five columns without a line to follow
    //   · an open row gets a filled surface AND an inset bar in the brand, which
    //     is what actually binds twenty lines of detail to the name above them
    // The bar is an inset box-shadow rather than a border so opening a row cannot
    // shift the column grid by 3px.
    <div
      style={{
        borderTop: `1px solid var(--ed-line-hover)`,
        // Three surfaces, not two: an open row must differ from BOTH zebra states
        // or it is only distinguishable on even rows.
        background: open ? "var(--ed-brand-soft)" : index % 2 ? "var(--ed-line-2)" : "transparent",
        boxShadow: open ? "inset 3px 0 0 var(--ed-brand)" : "none",
        paddingLeft: open ? 10 : 0,
        paddingRight: open ? 10 : 0,
        marginLeft: open ? -10 : 0,
        marginRight: open ? -10 : 0,
      }}
    >
      {/* ── TWO SIBLINGS, NOT A BUTTON INSIDE A BUTTON ────────────────────────────
          Clicking the row opens the detail; clicking the tick records that a person looked.
          Those must never be the same gesture, and nesting the controls would be invalid
          HTML as well as ambiguous. A flex row with the disclosure carrying its own column
          grid keeps them separate and keeps every column lined up with its header — the
          first attempt overlaid the tick with a negative margin, which lined up only by
          arithmetic and would have drifted the first time a column changed. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          flex: 1,
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: ROW_COLUMNS,
          gap: "0 12px",
          alignItems: "center",
          width: "100%",
          padding: "11px 2px",
          background: "none",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          font: "inherit",
          // A WORKED CHART STAYS ON THE LIST AND READS AS DONE. Removing it would take away
          // the only way she can check her own work, and on a screen whose point is "these
          // are the charts you need to look at", a chart that vanishes when ticked is a
          // chart she cannot prove she looked at.
          opacity: reviewed ? 0.62 : 1,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: T.sm, fontWeight: 700, color: "var(--ed-ink)", lineHeight: LH.tight }}>
            {who.name}
            {who.room ? <span style={{ color: "var(--ed-muted)", fontWeight: 600 }}> · {who.room}</span> : null}
          </div>
          {who.unresolved ? (
            <div style={{ fontSize: T.micro, color: "var(--ed-amber)", marginTop: 2 }}>
              PointClickCare id — this resident is not on the roster this screen loaded
            </div>
          ) : null}
          {row.synthetic ? (
            <div style={{ fontSize: T.micro, color: "var(--ed-amber-deep)", marginTop: 2 }}>
              an order here arrived with no identifier and was keyed by us
            </div>
          ) : null}
        </div>
        {legOrder.map((k) => {
          const l = row.legs?.[k];
          // ── FALLS BACK TO `state`, AND THAT IS NOT BELT AND BRACES ──────────
          //
          // This repository and the API deploy independently — Netlify on push, the
          // API through its own workflow — so for some window a browser running this
          // code talks to a server that has never heard of `readable`. Rendering
          // `undefined` would blank the column, which is the worst of the three
          // possible wrongs: a chart with a finding would look like a chart with
          // nothing. `?? l?.state` keeps the old single-mark behaviour until the
          // payload carries the new fields, so the ordering of the two deploys does
          // not matter.
          return (
            <div key={k}>
              <LegMark
                state={l?.readable ?? l?.state}
                blocked={l?.blocked ?? 0}
                blockedScopes={l?.blockedScopes ?? []}
              />
            </div>
          );
        })}
        <div style={{ fontSize: T.xs, color: "var(--ed-muted)", lineHeight: LH.body }}>{row.because}</div>
      </button>
        <div style={{ flex: `0 0 ${REVIEWED_COL}px` }}>
          <ReviewedButton reviewed={reviewed} busy={busy} onClick={() => onToggleReviewed(!reviewed)} />
        </div>
      </div>

      {/* THE FINDINGS, WITHOUT A SECOND CLICK. Below the grid rather than inside a column:
          they are sentences, and a sentence squeezed into a 104px mark column is a sentence
          nobody reads. Dimmed with the row when the chart has been reviewed, because a reviewed
          chart's findings are a record of what she dealt with rather than a call to act. */}
      {/* ABOVE the findings, because it is the one line that must be read first, and shown
          whether or not the row has any ATTENTION findings at all — a chart whose only
          activity is a new anticoagulant has none. Dimmed with the row once reviewed, like
          everything else: a worked chart's marks are a record, not a call to act. */}
      {highRisk.length && !open ? (
        <div style={{ margin: "-4px 0 9px 0", opacity: reviewed ? 0.62 : 1 }}>
          {highRisk.map((h, i) => (
            <div
              key={i}
              style={{
                display: "flex", gap: 7, alignItems: "flex-start",
                fontSize: T.xs, lineHeight: LH.body, color: "var(--ed-amber-deep)",
              }}
            >
              <HighRiskMark compact />
              <span style={{ minWidth: 0 }}>
                <strong>{h.order}</strong>
                {h.drugClass ? ` (${h.drugClass})` : ""} — high-alert class, read this order
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {findings.length && !open ? (
        <div style={{ margin: "-4px 0 9px 0", opacity: reviewed ? 0.62 : 1 }}>
          {findings.map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 7,
                alignItems: "flex-start",
                fontSize: T.xs,
                lineHeight: LH.body,
                color: "#7f1d1d",
              }}
            >
              <span aria-hidden="true" style={{ fontWeight: 800, color: "#b91c1c" }}>!</span>
              <span style={{ minWidth: 0 }}>{f}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* ── "SO WHAT HAPPENED?", ASKED ONLY WHERE IT HAS AN ANSWER ───────────────
          Offered after the chart is ticked, and only on charts the NIGHTLY ALERT reports —
          those are the ones that keep reaching an inbox for up to seven days, so they are
          the only ones where answering changes anything. Asking it of a chart nobody is
          being told about would be a question with no consequence, which is how a screen
          teaches people to ignore it.

          It records an ACTION and moves no mark. See DispositionPicker. */}
      {reviewed && alertWouldReport(row) ? (
        <DispositionPicker
          value={disposition}
          note={dispositionNote}
          busy={busy}
          unsaved={dispositionUnsaved}
          onChoose={(d, n) => onChooseDisposition(d, n)}
        />
      ) : null}

      {open ? (
        <div style={{ padding: "2px 0 13px 2px" }}>
          {legOrder.map((k) => {
            const leg = row.legs?.[k];
            if (!leg) return null;
            return (
              <div key={k} style={{ marginBottom: 9 }}>
                <div style={{ fontSize: T.micro, fontWeight: 800, color: "var(--ed-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                  {leg.label}
                </div>
                <div style={{ paddingLeft: 2 }}>
                  {(leg.checks || []).map((c) => <CheckLine key={c.id} check={c} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

// `roster` is this screen's own resident list, which is where names come from: no resident
// name leaves the server on this route. There is deliberately no `onOpenResident` -- a
// half-wired "open the chart" that lands somewhere unhelpful is worse than a list that is
// honestly just a list, and the resident detail is one click away in the tab beside this one.
export default function ChartCheckTab({ ccn, demo = false, roster = [] }) {
  const [state, setState] = useState({ loading: !demo });
  const [hours, setHours] = useState(24);
  const [reloadKey, setReloadKey] = useState(0);
  // ── THE TICKS, HELD HERE AND NOT RE-FETCHED ───────────────────────────────
  //
  // Optimistic: the tick moves on click and the request follows. On a failure it moves back
  // and the screen says so, because a tick that silently did not save is the one failure
  // this feature must not have — the manager believes the chart is recorded and only finds
  // out when it returns tomorrow.
  const [reviewed, setReviewed] = useState(() => new Set());
  const [markBusy, setMarkBusy] = useState(() => new Set());
  const [markError, setMarkError] = useState(null);
  // WHAT WAS DONE, held beside the ticks and keyed the same way: residentId -> {disposition,
  // note, unsaved}. `unsaved` is the third state — the tick was written and the answer was
  // not, which is what happens on a deployment where migration 074 has not been applied. It
  // is per row rather than one banner because it is a fact about that chart: that one is
  // still going to appear in tonight's alert.
  const [dispositions, setDispositions] = useState(() => new Map());

  // THE SAMPLE IS REAL OUTPUT OF buildChartCheck(), generated by
  // survai-engine/scripts/build_chart_check_sample.mjs and asserted by a drift test. It is
  // not hand-written JSON, so a leg state in the demo is a leg state the product emits.
  const load = useCallback(async () => {
    if (demo) {
      // The worked example's marks live on this device, against the example's own fixed
      // clock — see sampleReviewed.js for why a wall-clock expiry would make them vanish.
      setReviewed(loadSampleReviewed(SAMPLE.asOf));
      setDispositions(
        new Map([...loadSampleDispositions(SAMPLE.asOf)].map(([k, v]) => [k, { ...v, unsaved: null }])),
      );
      setState({ loading: false, data: SAMPLE, sample: true });
      return;
    }
    if (!ccn) {
      setState({ loading: false, error: "No facility is selected." });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await fetchChartCheck(ccn, { hours });
      if (r && r.locked) {
        setState({ loading: false, locked: true, reason: r.reason });
        return;
      }
      const data = r?.data || null;
      // SEEDED FROM THE SERVER'S ANSWER, not accumulated across reloads: the server decides
      // which marks are still inside the window, and a client-side set that survived a
      // reload would keep showing yesterday's ticks after they had expired.
      setReviewed(new Set((data?.residents || []).filter((x) => x.reviewed).map((x) => String(x.residentId))));
      // SEEDED FROM THE SERVER TOO, and for the same reason: the answer lives in the table
      // and the window decides which ones still count. A client-side map that survived a
      // reload would keep showing an answer the suppression no longer honours.
      setDispositions(
        new Map(
          (data?.residents || [])
            .filter((x) => x.reviewed?.disposition)
            .map((x) => [
              String(x.residentId),
              { disposition: x.reviewed.disposition, note: x.reviewed.dispositionNote ?? null, unsaved: null },
            ]),
        ),
      );
      setMarkError(null);
      setState({ loading: false, data });
    } catch (e) {
      // ── A FAILURE IS CARRIED, NOT SWALLOWED ────────────────────────────────
      //
      // "No charts to check this morning" and "we could not ask" look identical in an empty
      // list, and the first one makes a unit manager stop checking. The 409 the server
      // returns for an unresolved tenant is its own message and is shown as such.
      setState({
        loading: false,
        error: e?.message || "The chart check could not be read.",
        code: e?.code || null,
      });
    }
  }, [ccn, demo, hours]);

  /**
   * Tick or un-tick one chart.
   *
   * The sample writes to this device and the live screen writes to the server, and BOTH say
   * when the write did not happen. Reusing one handler keeps that promise in one place.
   */
  const toggleReviewed = useCallback(
    async (residentId, next) => {
      const id = String(residentId);
      setMarkError(null);
      setReviewed((prev) => {
        const s2 = new Set(prev);
        if (next) s2.add(id); else s2.delete(id);
        return s2;
      });
      // UN-TICKING DROPS THE ANSWER, because the server's un-mark deletes the rows inside the
      // window and the answer is a column on one of them. Keeping it on screen would show an
      // answer that no longer exists anywhere and that the nightly alert no longer honours.
      if (!next) {
        setDispositions((prev) => {
          const m = new Map(prev);
          m.delete(id);
          return m;
        });
        if (demo) setSampleDisposition(SAMPLE.asOf, id, null);
      }

      if (demo) {
        const { stored } = toggleSampleReviewed(SAMPLE.asOf, id, next);
        if (!stored) {
          setMarkError(
            "This browser would not store the tick, so it will be gone when you reload. " +
              "A private window with site data blocked does this.",
          );
        }
        return;
      }

      setMarkBusy((prev) => new Set(prev).add(id));
      try {
        const call = next ? markChartReviewed : unmarkChartReviewed;
        const r = await call(ccn, id, { hours });
        if (r && r.locked) throw new Error(r.reason || "This account is not in the preview.");
      } catch (e) {
        // PUT IT BACK. An optimistic tick that stays after the write failed is the specific
        // lie this feature must not tell.
        setReviewed((prev) => {
          const s2 = new Set(prev);
          if (next) s2.delete(id); else s2.add(id);
          return s2;
        });
        setMarkError(e?.message || "That did not save, so the chart is still on your list.");
      } finally {
        setMarkBusy((prev) => {
          const s2 = new Set(prev);
          s2.delete(id);
          return s2;
        });
      }
    },
    [ccn, demo, hours],
  );

  /**
   * Record what was done about a chart.
   *
   * ── IT WRITES A SECOND MARK; IT DOES NOT EDIT THE FIRST ───────────────────
   *
   * The API has no update: a tick is an insert and the read takes the newest row, so an
   * answer is a fresh mark carrying the answer. That is why this calls markChartReviewed
   * again rather than a PATCH, and why changing your mind leaves both rows in the table —
   * the history of who said what is the point of the column.
   *
   * ── THE THIRD STATE IS ON THE ROW, NOT IN A BANNER ────────────────────────
   *
   * "Saved" and "did not save" are the obvious two. The third is the tick saved and the
   * ANSWER did not, which is what a deployment without migration 074 does, and it matters per
   * chart: that one is still going to appear in tonight's alert. So the row says so.
   */
  const chooseDisposition = useCallback(
    async (residentId, disposition, note) => {
      const id = String(residentId);
      setMarkError(null);
      const before = dispositions.get(id) ?? null;
      setDispositions((prev) => new Map(prev).set(id, { disposition, note: note || null, unsaved: null }));

      if (demo) {
        const { stored } = setSampleDisposition(SAMPLE.asOf, id, disposition, note);
        if (!stored) {
          setMarkError(
            "This browser would not store the answer, so it will be gone when you reload. " +
              "A private window with site data blocked does this.",
          );
        }
        return;
      }

      setMarkBusy((prev) => new Set(prev).add(id));
      try {
        const r = await markChartReviewed(ccn, id, { hours, disposition, dispositionNote: note });
        if (r && r.locked) throw new Error(r.reason || "This account is not in the preview.");
        // THE SERVER SAYS WHETHER THE ANSWER LANDED, separately from whether the tick did.
        // Trusting the 200 alone would show an answer that is not in the table.
        if (r?.data && r.data.dispositionStored === false) {
          setDispositions((prev) =>
            new Map(prev).set(id, {
              disposition,
              note: note || null,
              unsaved: r.data.dispositionWhy || "The column it needs does not exist on this deployment yet.",
            }),
          );
        }
      } catch (e) {
        // PUT IT BACK, exactly as the tick does. An answer left on screen after the write
        // failed is the same lie in a different place.
        setDispositions((prev) => {
          const m = new Map(prev);
          if (before) m.set(id, before); else m.delete(id);
          return m;
        });
        setMarkError(e?.message || "That answer did not save, so this chart is still on tonight's alert.");
      } finally {
        setMarkBusy((prev) => {
          const s2 = new Set(prev);
          s2.delete(id);
          return s2;
        });
      }
    },
    [ccn, demo, hours, dispositions],
  );

  // `load` IS THE DEPENDENCY, not its inputs copied out again. It is a useCallback over
  // [ccn, demo, hours], so depending on it covers all three and cannot fall behind when a
  // fourth is added -- which is how an effect ends up reading a stale facility. `reloadKey`
  // is the one thing not in load's own closure: it exists only to re-run this.
  useEffect(() => { load(); }, [load, reloadKey]);

  const d = state.data;
  // ── THE COUNT HAS TO INCLUDE THE TICK THAT WAS JUST MADE ──────────────────
  //
  // `counts.reviewed` and `counts.remaining` arrive with the response, so reading them
  // directly meant the headline ignored every tick until a reload — the manager working down
  // the list would watch "8 left" stay at 8 while rows went green behind it. The worksheet
  // already learned this: liveCoverage recomputes the percentage from the unsaved drafts for
  // exactly the same reason.
  //
  // Everything else on the headline is the server's. Only the two numbers a click changes
  // are recomputed here.
  const live = useMemo(() => {
    if (!d) return d;
    const rows = d.residents || [];
    const done = rows.filter((r) => reviewed.has(String(r.residentId))).length;
    return { ...d, counts: { ...(d.counts || {}), reviewed: done, remaining: rows.length - done } };
  }, [d, reviewed]);
  const headline = useMemo(() => chartCheckHeadline(live), [live]);
  // The server's own column order, so a client that has not been redeployed cannot show the
  // three legs in an order the data does not use.
  const legOrder = useMemo(() => (d?.legs || []).map((l) => l.key), [d]);
  // How many questions the facility-wide blocked leg would answer. Read off a real chart
  // rather than typed into the sentence — see the note where it is rendered.
  const blockedLegQuestions = useMemo(() => {
    const key = (d?.legs || []).find((l) => l.state === LEG_STATE.NOT_CHECKED)?.key;
    const row = (d?.residents || []).find((r) => r.legs?.[key]);
    return key && row ? (row.legs[key].checks || []).length : 0;
  }, [d]);

  const shell = (children) => (
    <div>
      {/* IN THE SHELL, so it is present on every return path. The loading, error, locked and
          empty states all go through here, and a keyframe defined only on the happy path is
          a mark that animates once the list arrives and not a moment before. */}
      <style>{HIGH_RISK_CSS}</style>
      <Header
        hours={hours}
        onHours={setHours}
        onReload={() => setReloadKey((k) => k + 1)}
        busy={!!state.loading}
        sample={!!state.sample}
      />
      {children}
    </div>
  );

  if (state.loading) return shell(<Muted>Reading the last 24 hours…</Muted>);
  if (state.locked) {
    return shell(
      <Notice tone="locked">
        The 24-hour chart check is in preview and is not enabled for this account.
        {state.reason ? <> {state.reason}</> : null}
      </Notice>,
    );
  }
  if (state.error) {
    return shell(
      <Notice tone="error">
        <strong>The chart check could not be read, so this is not an empty morning.</strong>{" "}
        {state.error}
        {state.code === "tenant_unresolved" ? (
          <div style={{ marginTop: 6 }}>
            This facility is not linked to a PointClickCare organisation. Until it is, its orders
            and notes cannot be read without risking another tenant's records, which is why
            nothing is shown rather than an empty list.
          </div>
        ) : null}
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} style={BTN}>Try again</button>
        </div>
      </Notice>,
    );
  }
  if (!d) return shell(<Muted>Nothing was returned.</Muted>);

  return shell(
    <div>
      {/* The one line at the top, and it is three different sentences rather than one with a
          number in it — see chartCheckHeadline. */}
      <div
        style={{
          padding: "11px 13px",
          borderRadius: 10,
          marginBottom: 11,
          lineHeight: LH.body,
          fontSize: T.base,
          ...(headline.tone === "work"
            ? { background: "#fff7ed", border: "1px solid #fed7aa", color: "#7c2d12" }
            : headline.tone === "done"
              ? { background: "#ecfdf5", border: "1px solid #6ee7b7", color: "#065f46" }
              : headline.tone === "quiet"
                ? { background: "var(--ed-success-soft)", border: "1px solid var(--ed-success-line)", color: "#14532d" }
                // AMBER, like every other "we cannot tell you" on this screen. The
                // headline's unknown tone is the same claim the blocked column makes —
                // no baseline yet, or a scan too old to trust — and it was the last
                // violet left over from when NOT_CHECKED was violet.
                // Same three values, same reason as the mark above.
                : { background: "var(--ed-amber-soft)", border: "1px solid var(--ed-amber-line)", color: "var(--ed-amber-deep)" }),
        }}
      >
        {headline.text}
      </div>

      {/* THE LABS LEG, SAID AT FACILITY LEVEL. It is the same answer on every chart, which is
          exactly why it belongs up here: a manager must be able to see the column is not
          being checked without opening a single chart. */}
      {(d.legs || []).filter((l) => l.state === LEG_STATE.NOT_CHECKED).map((l) => (
        <div
          key={l.key}
          style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            padding: "10px 13px", borderRadius: 10, marginBottom: 11,
            // FROM THE MARK, NOT A SECOND COPY OF IT.
            //
            // This banner used to hold #f5f3ff/#ddd6fe/#5b21b6 — the violet that
            // NOT_CHECKED used to be. When that state became amber in
            // chartCheckLegs.js, this did not follow, so the same state rendered
            // amber in the table and violet in the banner directly above it. A
            // duplicated colour is a colour that will disagree with itself; taking
            // it from legMark() is the only version that cannot.
            background: legMark(l.state).tone.bg,
            border: `1px solid ${legMark(l.state).tone.border}`,
            color: legMark(l.state).tone.fg,
            fontSize: T.sm, lineHeight: LH.body,
          }}
        >
          <LegMark state={l.state} />
          <div>
            {/* THE COUNT IS DERIVED, NOT TYPED. It said "the four questions" and the leg grew a
                fifth the day a lab ORDER was recognised as a different fact from a lab RESULT.
                A hardcoded number in a sentence about completeness is the one place it is
                least affordable to be wrong. */}
            <strong>{l.label}</strong> are not being checked on any chart here, for every resident in
            the building. This is our integration, not the chart: no PointClickCare endpoint for lab
            orders or lab results is granted to us, and no date is set.
            {blockedLegQuestions ? (
              <> Open a chart to see the {blockedLegQuestions} questions it would answer.</>
            ) : null}
          </div>
        </div>
      ))}

      {markError ? (
        <Notice tone="error">
          <strong>That tick did not save.</strong> {markError}
        </Notice>
      ) : null}

      {/* "We could not read the marks" and "nobody has reviewed anything" look identical in an
          empty result, and the first sends a manager down a list she has already done. */}
      {d.reviewedReadFailed ? (
        <Notice tone="error">
          <strong>We could not read which charts have already been reviewed</strong>, so every row
          below shows as not yet reviewed whether or not it is. {d.reviewedReadFailed}
        </Notice>
      ) : null}

      {!d.mirrorHasNotes ? (
        <Notice tone="error">
          <strong>We hold no progress notes for this facility at all.</strong> An empty note column
          below is our gap and not the chart's — the nightly scan writes that index, so check that it
          has run before reading any of this as a quiet night.
        </Notice>
      ) : null}

      <div style={{ padding: "4px 0 10px" }}><StateLegend /></div>

      <div style={{ border: "1px solid var(--ed-line)", borderRadius: 12, padding: "2px 13px 4px", background: "var(--ed-canvas)" }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, padding: "9px 2px 5px" }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "grid",
              gridTemplateColumns: ROW_COLUMNS,
              gap: "0 12px",
              fontSize: T.micro,
              fontWeight: 800,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: "var(--ed-muted)",
            }}
          >
            <div>Resident</div>
            {(d.legs || []).map((l) => <div key={l.key}>{l.label}</div>)}
            <div>On the list because</div>
          </div>
          <div
            style={{
              flex: `0 0 ${REVIEWED_COL}px`,
              fontSize: T.micro, fontWeight: 800, letterSpacing: ".05em",
              textTransform: "uppercase", color: "var(--ed-muted)", textAlign: "center",
            }}
            title="A tick records that you looked. It answers nothing and enters no percentage."
          >
            Reviewed
          </div>
        </div>
        {d.residents?.length ? (
          // NO .sort() HERE, DELIBERATELY. "You don't pick. That's the list you work with."
          // A sort becomes a default order, a default order becomes a priority, and a
          // priority becomes a cut-off — which is this screen choosing her ten charts.
          d.residents.map((r, i) => (
            <ChartRow
              key={r.residentId}
              row={r}
              index={i}
              roster={roster}
              legOrder={legOrder}
              reviewed={reviewed.has(String(r.residentId))}
              busy={markBusy.has(String(r.residentId))}
              onToggleReviewed={(next) => toggleReviewed(r.residentId, next)}
              disposition={dispositions.get(String(r.residentId))?.disposition ?? null}
              dispositionNote={dispositions.get(String(r.residentId))?.note ?? null}
              dispositionUnsaved={dispositions.get(String(r.residentId))?.unsaved ?? null}
              onChooseDisposition={(d, n) => chooseDisposition(r.residentId, d, n)}
            />
          ))
        ) : (
          <div style={{ padding: "16px 2px", fontSize: T.sm, color: "var(--ed-muted)", lineHeight: LH.body }}>
            No resident had a medication or order written or a progress note added in this window.
            That is the chart check passing, not a screen with nothing in it — and the labs column
            above is still not being checked, as are orders beyond medications.
          </div>
        )}
      </div>

      <Freshness data={d} sample={!!state.sample} />
    </div>,
  );
}

const BTN = {
  padding: "6px 11px",
  borderRadius: 8,
  border: "1px solid var(--ed-line)",
  background: "var(--ed-canvas)",
  color: "var(--ed-ink)",
  fontSize: T.xs,
  fontWeight: 700,
  cursor: "pointer",
};

const Header = ({ hours, onHours, onReload, busy, sample }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={{ fontSize: T.xl, fontWeight: 800, color: "var(--ed-ink)", lineHeight: LH.tight }}>
        24-hour chart check
      </div>
      <div style={{ fontSize: T.sm, color: "var(--ed-ink-2)", marginTop: 3, lineHeight: LH.body }}>
        {/* ── "AN ORDER" WAS TOO BROAD, AND THIS IS THE SENTENCE THAT SET THE EXPECTATION ──
            /medications is approved and is a real route of entry for orders, so the first
            draft said "an order written". A manager reads that as every order in the
            building. What reaches this list is the medications/orders list — ONE route of
            entry carrying both, which is why the column is named for it rather than for
            either half. The total view — general, treatment, diet, therapy, and the lab
            orders — arrives November 2026 alongside care plans, and the column carries that
            as its own unchecked question. The headline says it once so nobody has to open a
            chart to find out. */}
        Every resident with a <strong>medication or order</strong> written or a progress note added in
        the window — the three-point check, on the charts that had activity. The list is not chosen and
        not ranked. Orders beyond medications, and lab orders, are not readable yet: November 2026.
      </div>
      {/* Collapsed. The named risk on this screen is overwhelm, and nine paragraphs between
          a manager and her worklist every morning is the thing that causes it. */}
      <ChartCheckExplainer />
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <label style={{ fontSize: T.micro, fontWeight: 800, color: "var(--ed-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>
        Window
      </label>
      <select
        value={hours}
        onChange={(e) => onHours(Number(e.target.value))}
        disabled={sample}
        title={sample ? "The worked example is built for one fixed 24-hour window" : "How far back to look"}
        style={{ ...BTN, padding: "6px 8px", fontFamily: "inherit" }}
      >
        {WINDOWS.map((w) => <option key={w.hours} value={w.hours}>{w.label}</option>)}
      </select>
      <button type="button" onClick={onReload} disabled={busy} style={{ ...BTN, opacity: busy ? 0.6 : 1 }}>
        {busy ? "Reading…" : "Reload"}
      </button>
    </div>
  </div>
);

/**
 * HOW FRESH THIS IS, SAID PLAINLY AND ALWAYS.
 *
 * This screen reads our own mirror, not PointClickCare. That is what makes it free to open
 * every morning, and it means the list is exactly as current as the last nightly scan. A
 * screen that implies otherwise is the silent failure this product keeps finding, so the
 * sentence is never hidden behind a tooltip and never omitted when the news is good.
 */
const Freshness = ({ data, sample }) => {
  const h = Number(data?.freshness?.hoursSinceNewestOrder);
  const w = data?.window || {};
  return (
    <div style={{ fontSize: T.xs, color: "var(--ed-muted)", marginTop: 10, lineHeight: LH.body }}>
      {sample ? (
        <>
          <strong>Worked example.</strong> The charts are invented; every mark, reason and count is
          real output of the chart-check builder, generated by its script and checked by a drift
          test. The window is fixed at the example's own clock rather than today's.
        </>
      ) : (
        <>
          Read from our own copy of this facility's orders and progress notes, written by the nightly
          scan — so it costs no PointClickCare call and it is exactly as current as that scan.{" "}
          {Number.isFinite(h)
            ? <>The newest order we hold was first seen <strong>{h} hours ago</strong>.</>
            : <>We hold no order in this window, so there is nothing to date this list from.</>}
          {data?.baseline === false ? (
            <> We have not held this facility's orders long enough to tell new from existing.</>
          ) : null}
        </>
      )}
      {w.clamped ? <> The window was narrowed to the {Math.round(w.clamped / 24)}-day maximum.</> : null}
      {w.since ? <> Window: {String(w.since).slice(0, 16).replace("T", " ")} to {String(w.until).slice(0, 16).replace("T", " ")} UTC.</> : null}
    </div>
  );
};

const Muted = ({ children }) => (
  <div style={{ padding: "18px 2px", fontSize: T.sm, color: "var(--ed-muted)" }}>{children}</div>
);

const Notice = ({ tone, children }) => (
  <div
    style={{
      padding: "11px 13px",
      borderRadius: 10,
      marginBottom: 11,
      fontSize: T.sm,
      lineHeight: LH.body,
      ...(tone === "error"
        ? { background: "var(--ed-alert-soft)", border: "1px solid var(--ed-alert-line)", color: "#7f1d1d" }
        : { background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)", color: "#1e3a8a" }),
    }}
  >
    {children}
  </div>
);
